import { TeethSelect } from "@jasper/forms";
import { Stack } from "@mantine/core";
import { useForm } from "@tanstack/react-form";
import { useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { type ItemType } from "@jasper/client-models";
import type { UpdateProductArgs } from "@jasper/use-cases";

import { useStepBulkGroup } from "../../hooks/useStepBulkGroup";
import type { ConfigurableProduct, ProductStepProps } from "../types";
import { useTeethSelectionSubmission } from "./useTeethSelectionSubmission";
import { useRegisterStepSave } from "../useRegisterStepSave";
import { useTeethSelectionDescription } from "./useTeethSelectionDescription";

type TeethValue = {
  teeth: number[];
  groupedTeeth: number[][];
};

export type TeethStepValidatorInput = {
  value: TeethValue;
  isBulkSelection: boolean;
  setTeethInError: Dispatch<SetStateAction<number[]>>;
};

type TeethStepValidator = (
  input: TeethStepValidatorInput
) => Record<string, unknown> | undefined;

type GenericTeethStepProps = ProductStepProps & {
  validateSelection: TeethStepValidator;
  groupAdjacentByDefault?: boolean;
  onSave: (teeth: number[]) => Promise<void>;
  applyTeethSelection: (args: {
    teeth: number[];
    product: ConfigurableProduct;
    itemType: ItemType;
  }) => Promise<UpdateProductArgs> | UpdateProductArgs;
  validateOnChange?: boolean;
  hideGroupAllButtons?: boolean;
  getTeethDescription?: (args: {
    teeth: number[];
    groupedTeeth?: number[][];
    descriptionLevel: "global" | "group";
  }) => { badgeContent: string; description: string };
  solidarizedLabel?: string;
};

export const GenericTeethStep = ({
  stepKey,
  product,
  bulkGroup,
  bulk,
  validateSelection,
  groupAdjacentByDefault = false,
  applyTeethSelection,
  onSave,
  validateOnChange,
  registerStepSave,
  itemType,
  hideGroupAllButtons,
  getTeethDescription,
  solidarizedLabel,
}: GenericTeethStepProps) => {
  const { t } = useTranslation("product-workflows", {
    keyPrefix: "steps.teeth",
  });
  const describeTeethSelection = useTeethSelectionDescription();

  const isBulkSelection = Boolean(bulk?.isSupported);

  const { isBulkMode, groupTeeth } = useStepBulkGroup(product, bulkGroup);

  const currentTeeth = isBulkSelection ? groupTeeth : product.teeth;

  const initialTeeth = useMemo(
    () =>
      isBulkSelection && bulk?.preselectedTeeth?.length
        ? bulk.preselectedTeeth
        : currentTeeth,
    [isBulkSelection, bulk?.preselectedTeeth, currentTeeth]
  );

  const initialGroupedTeeth = useMemo(
    () =>
      isBulkSelection && bulk?.isSupported && bulkGroup
        ? bulkGroup.groupProducts.map(product => product.teeth)
        : product.teeth.length
          ? [product.teeth]
          : [],
    [isBulkSelection, bulk?.isSupported, bulkGroup, product.teeth]
  );

  // This is only used in bulk mode. In this mode some specific teeth can be in error while in normal mode, it is all or nothing.
  const [teethInError, setTeethInError] = useState<number[]>([]);

  const submitTeethSelection = useTeethSelectionSubmission({
    currentGroupedTeeth: initialGroupedTeeth,
    isBulkSelection,
    isBulkMode,
    bulk,
  });

  const defaultValues = useMemo(
    () => ({
      product: {
        teeth: [...initialTeeth],
        groupedTeeth: [...initialGroupedTeeth],
      },
    }),
    [initialTeeth, initialGroupedTeeth]
  );

  const form = useForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      await submitTeethSelection(
        value.product.groupedTeeth,
        applyTeethSelection,
        () => onSave(value.product.teeth)
      );
    },
    listeners: {
      onChangeDebounceMs: 250,
      onBlurDebounceMs: 250,
      onChange: ({ formApi }) => {
        if (
          !formApi.state.isValid ||
          formApi.state.isSubmitting ||
          formApi.state.isPristine
        )
          return;
        formApi.handleSubmit();
      },
      onBlur: ({ formApi }) => {
        if (
          !formApi.state.isValid ||
          formApi.state.isSubmitting ||
          formApi.state.isPristine
        )
          return;
        formApi.handleSubmit();
      },
    },
  });

  useRegisterStepSave({
    stepKey,
    registerStepSave,
    form,
  });

  const handleToothClick = ({
    toothId,
    selectedTeeth,
    setSelectedTeeth,
  }: {
    toothId: number;
    selectedTeeth: number[];
    setSelectedTeeth: (teeth: number[]) => void;
  }) => {
    if (selectedTeeth.includes(toothId)) {
      setSelectedTeeth(selectedTeeth.filter(id => id !== toothId));
    } else {
      setSelectedTeeth([...selectedTeeth, toothId]);
    }
  };

  return (
    <Stack gap="md" flex={1} mih={0} h="100%" p="md">
      <form.Field
        name="product"
        listeners={{
          onChange: ({ fieldApi }) => {
            fieldApi.setErrorMap({
              onSubmit: undefined,
              onBlur: undefined,
            });
          },
        }}
        validators={{
          onChange: ({ value, fieldApi }) => {
            const res = validateSelection({
              value,
              isBulkSelection,
              setTeethInError,
            });

            if (!res) {
              fieldApi.setErrorMap({
                onBlur: undefined,
                onSubmit: undefined,
              });
            }

            return res;
          },

          onBlur: ({ value }) =>
            validateSelection({
              value,
              isBulkSelection,
              setTeethInError,
            }),
          onSubmit: ({ value }) =>
            validateSelection({
              value,
              isBulkSelection,
              setTeethInError,
            }),
        }}
      >
        {field => {
          const formErrors =
            field.state.meta.errorMap.onBlur ??
            field.state.meta.errorMap.onSubmit ??
            (validateOnChange ? field.state.meta.errorMap.onChange : undefined);

          const errors =
            typeof formErrors?.teeth === "string"
              ? t(formErrors?.teeth, {
                  keyPrefix: "steps.teeth.errors",
                  defaultValue: t("errorOccurred", { keyPrefix: "common" }),
                })
              : undefined;

          const getTeethInError = () => {
            if (!errors) {
              return [];
            }

            if (teethInError.length) {
              return teethInError;
            }

            return field.state.value.teeth;
          };

          return (
            <Stack>
              <TeethSelect
                onBlur={() => field.handleBlur()}
                teeth={field.state.value.teeth}
                groupedTeeth={field.state.value.groupedTeeth}
                error={errors}
                teethError={getTeethInError()}
                withGrouping
                hideGroupAllButtons={hideGroupAllButtons}
                groupAdjacentByDefault={groupAdjacentByDefault}
                solidarizedLabel={solidarizedLabel}
                onClick={(toothId, newGroups) => {
                  setTeethInError([]);
                  handleToothClick({
                    toothId,
                    selectedTeeth: field.state.value.teeth,
                    setSelectedTeeth: teeth =>
                      field.setValue({
                        ...field.state.value,
                        teeth,
                        groupedTeeth: newGroups,
                      }),
                  });
                }}
                onGroupedTeethChange={newGroups => {
                  setTeethInError([]);
                  field.setValue({
                    ...field.state.value,
                    groupedTeeth: newGroups,
                  });
                }}
                getTeethDescription={
                  getTeethDescription ??
                  (({ teeth }) =>
                    describeTeethSelection(teeth, [
                      {
                        type: itemType,
                        count: teeth.length,
                      },
                    ]))
                }
              />
            </Stack>
          );
        }}
      </form.Field>
    </Stack>
  );
};
